import { Controller } from "@hotwired/stimulus"

// Controls the "Importar arquivos" modal on an Application.
//
// Targets:
//   dropzone     — the drag/drop area
//   input        — hidden <input type="file" multiple>
//   fileList     — <ul> where each picked file is rendered as a row
//   rowTemplate  — <template> for a single file row
//   submitButton — disabled until at least one file is queued
//
// Behavior:
//   - Picks files via click or drag-and-drop.
//   - Accepts: PDF, DOCX/XLSX/PPTX, ODT/ODS, DOC, TXT/MD/CSV, images.
//   - On submit, builds a DataTransfer with all picked files and assigns it
//     to the hidden <input type="file" name="files[]" multiple> so the
//     server receives a multi-file upload in a single request.
export default class extends Controller {
  static targets = ["dropzone", "input", "fileList", "rowTemplate", "submitButton"]

  static values = {
    accept: {
      type: String,
      default: [
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        "application/vnd.oasis.opendocument.text",
        "application/vnd.oasis.opendocument.spreadsheet",
        "application/msword",
        "text/plain",
        "text/markdown",
        "text/x-markdown",
        "text/csv",
        "image/jpeg",
        "image/png",
        "image/webp",
        "image/heic",
        "image/tiff"
      ].join(",")
    }
  }

  files = []
  counter = 0

  connect() {
    this._refreshSubmit()
  }

  dragover(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("border-zinc-500", "bg-zinc-50")
  }

  dragleave(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-zinc-500", "bg-zinc-50")
  }

  drop(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-zinc-500", "bg-zinc-50")

    const accepted = this.acceptValue.split(",").map(s => s.trim())
    Array.from(event.dataTransfer.files).forEach(file => {
      if (this._isAccepted(file, accepted)) this._addFile(file)
    })
  }

  pickFiles() {
    this.inputTarget.click()
  }

  filesPicked(event) {
    Array.from(event.target.files).forEach(file => this._addFile(file))
    event.target.value = ""
  }

  removeFile(event) {
    event.preventDefault()
    const row = event.target.closest("[data-file-row]")
    if (!row) return
    const index = parseInt(row.dataset.fileIndex, 10)
    this.files = this.files.filter(entry => entry.index !== index)
    row.remove()
    this._refreshSubmit()
  }

  // Called via data-action="submit->application-import#beforeSubmit"
  beforeSubmit(event) {
    if (this.files.length === 0) {
      event.preventDefault()
      return
    }

    // Pack the picked files into the hidden input as a FileList.
    const dt = new DataTransfer()
    this.files.forEach(entry => dt.items.add(entry.file))
    this.inputTarget.files = dt.files
  }

  _isAccepted(file, acceptedList) {
    if (acceptedList.length === 0) return true
    // Accept by MIME type OR by extension fallback (some browsers omit type for .md/.txt).
    return acceptedList.includes(file.type) || this._isAcceptedByExtension(file.name)
  }

  _isAcceptedByExtension(name) {
    return /\.(pdf|docx?|xlsx?|pptx?|odt|ods|txt|md|csv|jpe?g|png|webp|heic|tiff?)$/i.test(name)
  }

  _addFile(file) {
    const index = this.counter++
    this.files.push({ file, index })

    const template = this.rowTemplateTarget.content.cloneNode(true)
    const row = template.querySelector("[data-file-row]")
    row.dataset.fileIndex = index
    row.querySelector("[data-file-name]").textContent = file.name
    row.querySelector("[data-file-size]").textContent = this._formatSize(file.size)
    this.fileListTarget.appendChild(row)
    this.fileListTarget.classList.remove("hidden")
    this._refreshSubmit()
  }

  _refreshSubmit() {
    if (!this.hasSubmitButtonTarget) return
    this.submitButtonTarget.disabled = this.files.length === 0
  }

  _formatSize(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / 1048576).toFixed(1)} MB`
  }
}
